/**
 * <InlineAlert> — persistent in-page alert with icon, title, optional subtext.
 *
 * Pattern: blocking errors stay visible inline (vs. toast which dismisses).
 * Used at the top of forms, beneath page headings, in empty states.
 */
import React from 'react';
import styles from './InlineAlert.module.css';

export type AlertVariant = 'danger' | 'warning' | 'success' | 'info';

interface InlineAlertProps {
  variant?: AlertVariant;
  title: React.ReactNode;
  description?: React.ReactNode;
  onDismiss?: () => void;
  children?: React.ReactNode;
  className?: string;
}

export const InlineAlert: React.FC<InlineAlertProps> = ({
  variant = 'danger',
  title,
  description,
  onDismiss,
  children,
  className = '',
}) => {
  return (
    <div
      className={`${styles.alert} ${styles[`variant_${variant}`]} ${className}`}
      role={variant === 'danger' || variant === 'warning' ? 'alert' : 'status'}
    >
      <span className={styles.icon} aria-hidden="true">
        <AlertIcon variant={variant} />
      </span>
      <div className={styles.body}>
        <div className={styles.title}>{title}</div>
        {description && <div className={styles.description}>{description}</div>}
        {children && <div className={styles.children}>{children}</div>}
      </div>
      {onDismiss && (
        <button
          type="button"
          className={styles.dismiss}
          onClick={onDismiss}
          aria-label="Dismiss"
        >
          ×
        </button>
      )}
    </div>
  );
};

const AlertIcon: React.FC<{ variant: AlertVariant }> = ({ variant }) => {
  if (variant === 'success') {
    return (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
           stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 12l2 2 4-4" /><circle cx="12" cy="12" r="9" />
      </svg>
    );
  }
  if (variant === 'info') {
    return (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
           stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="9" /><path d="M12 8h.01M11 12h1v4h1" />
      </svg>
    );
  }
  // danger + warning use the triangle
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
};
